import { useMediaQuery } from '@material-ui/core'
import { LAMPORTS_PER_SOL } from '@solana/web3.js'
import { useAtom } from 'jotai'
import { useRouter } from 'next/router'
import * as React from 'react'
import styled from 'styled-components'
import { LeaderboardTime } from '..'
import { rwSolPriceAtom } from '../../../atoms/WalletName'
import { CollectorLeaderboardUser } from '../../../interfaces/User'
import ProfileImage from '../../profile/image'
import { CollectorSelectedFilter } from '../TrendingCollectors'

const StyledRow = styled.tr`
  width: 100%;
  border: none;
  border-spacing: 0;

  td {
    background: linear-gradient(rgb(45, 45, 45), rgb(20, 20, 20));
    border-color: #323232;
    border-width: 1px;
    border-style: solid;
    border-left: none;
    border-right: none;
    color: white;
    font-size: 24px;
    font-weight: 700;
    white-space: nowrap;

    @media (max-width: 62em) {
      text-align: right;
      padding-right: 15px;
    }

    @media (max-width: 43em) {
      font-size: 18px;
    }
  }

  td:first-child {
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
    border-left: 1px solid #323232;
  }

  td:last-child {
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
    border-right: 1px solid #323232;
  }
`

const CollectorCell = styled.div`
  padding: 15px 0;
  margin-left: 15px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 25px;
  cursor: pointer;

  @media (max-width: 43em) {
    gap: 15px;
  }
`

const Rank = styled.div`
  color: white;
  font-size: 18px;
  font-weight: 700;
  text-align: left;
  width: 25px;

  @media (max-width: 43em) {
    font-size: 14px;
  }
`

const CollectorName = styled.div`
  color: white;
  font-size: 18px;
  font-weight: 700;
  text-align: left;

  @media (max-width: 43em) {
    max-width: 100px;
    font-size: 14px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

const ValueCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  max-width: 100px;
`

const Subtitle = styled.div`
  color: #aaa;
  font-weight: 500;
  font-size: 14px;

  @media (max-width: 43em) {
    font-size: 12px;
  }
`

const CollectorRow = ({
  id,
  user,
  leaderboardInfo,
  rank,
  time,
  filter,
}: CollectorLeaderboardUser & {
  rank: number
  time: LeaderboardTime
  filter: CollectorSelectedFilter
}): JSX.Element => {
  const [price, setPrice] = useAtom(rwSolPriceAtom)
  const isTablet = useMediaQuery('(max-width: 62em)')
  const isMobile = useMediaQuery('(max-width: 43em)')
  const [userHover, setUserHover] = React.useState(false)
  const router = useRouter()

  const getSolPrice = async (): Promise<void> => {
    const resp = await fetch(
      'https://api.coingecko.com/api/v3/simple/price?vs_currencies=usd&ids=solana'
    )
    const json = await resp.json()
    setPrice(json['solana']['usd'] as number)
  }

  React.useEffect(() => {
    if (price === 0) {
      getSolPrice()
    }
  }, [])

  const hoverStyle = {
    textDecoration: userHover ? 'underline' : 'none',
  }

  const handleClick = (e: React.MouseEvent): void => {
    e.stopPropagation()
    router.push(`/${id}`)
  }

  const collected =
    time === LeaderboardTime.ALL
      ? leaderboardInfo?.allCollected
      : time === LeaderboardTime.MONTH
      ? leaderboardInfo?.monthCollected
      : leaderboardInfo?.weekCollected

  const spent =
    time === LeaderboardTime.ALL
      ? leaderboardInfo?.allSpent
      : time === LeaderboardTime.MONTH
      ? leaderboardInfo?.monthSpent
      : leaderboardInfo?.weekSpent

  if (isTablet) {
    return (
      <StyledRow>
        <td>
          <CollectorCell
            onClick={handleClick}
            onMouseEnter={() => setUserHover(true)}
            onMouseLeave={() => setUserHover(false)}
          >
            <Rank>{'#' + rank}</Rank>
            <ProfileImage item={user} size={isMobile ? 45 : 60} />
            <CollectorName style={hoverStyle}>{user?.displayName}</CollectorName>
          </CollectorCell>
        </td>
        {filter === CollectorSelectedFilter.COLLECTED ? (
          <td>{collected}</td>
        ) : (
          <td>
            <ValueCell>
              {Math.round((spent / LAMPORTS_PER_SOL) * 100) / 100} SOL
              <Subtitle>${((spent / LAMPORTS_PER_SOL) * price).toFixed(2)}</Subtitle>
            </ValueCell>
          </td>
        )}
      </StyledRow>
    )
  }

  return (
    <StyledRow>
      <td>
        <CollectorCell
          onClick={handleClick}
          onMouseEnter={() => setUserHover(true)}
          onMouseLeave={() => setUserHover(false)}
        >
          <Rank>{'#' + rank}</Rank>
          <ProfileImage item={user} size={isMobile ? 45 : 60} />
          <CollectorName style={hoverStyle}>{user?.displayName}</CollectorName>
        </CollectorCell>
      </td>
      <td>
        <ValueCell>
          {Math.round((spent / LAMPORTS_PER_SOL) * 100) / 100} SOL
          <Subtitle>${((spent / LAMPORTS_PER_SOL) * price).toFixed(2)}</Subtitle>
        </ValueCell>
      </td>
      <td>{collected}</td>
    </StyledRow>
  )
}

export default CollectorRow
